import { FC, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BackIcon } from 'assets/icons';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import routesPath from 'routes/RoutesPath';
import {
  useCreateCommissionRuleMutation,
  useLazyGetCommissionRuleByIdQuery,
  useUpdateCommissionRuleMutation
} from './api';
import CommissionConditions from '../components/CommissionConditions';
import CommissionRate from '../components/CommissionRate';
import AssignTeamMembers from '../components/AssignTeamMembers';
import CommissionCalculationExample from '../components/CommissionCalculationExample';
import { AssignableMember } from 'containers/jobs/jobs.model';
import { useAppSelector } from 'hooks';
import { selectCommissionBaseMap, selectCurrentTeam } from 'containers/app/appSlice';
import { useLazyGetAssignableMembersQuery } from 'containers/jobs/api';
import CustomInput from 'components/custom-input.tsx/CustomInput';
import { CommissionStatus, CommissionType } from './constants';
import {
  CommissionRuleCondition,
  CreateCommissionRulePayload,
  UpdateCommissionRulePayload
} from './commissionSettings.model';
import Loader from 'components/loader/Loader';
import AssignableMembersModal from 'containers/jobs/create-edit-job/components/AssignableMembersModal';
import { useGetExternalCompaniesQuery } from '../external-company-settings/api';
import { logAnalyticEvent } from 'utils/analytics';
import { SettingsCleverTapEvents } from '../events';
import { useLazyGetMerchantCustomizationsQuery } from '../api';
import { useLazyGetCommissionBaseQuery } from 'containers/app/api';

enum SectionIds {
  CONDITIONS = 'conditions',
  COMMISSION_RATE = 'commission-rate',
  MEMBERS = 'members',
  TITLE = 'title'
}

const sections = [
  { id: SectionIds.CONDITIONS, label: 'Conditions' },
  { id: SectionIds.COMMISSION_RATE, label: 'Commission rate' },
  { id: SectionIds.MEMBERS, label: 'Members' },
  { id: SectionIds.TITLE, label: 'Title' }
];

const CreateCommissionRule: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isEditMode = location.pathname.includes('edit-rule');

  const currentTeam = useAppSelector(selectCurrentTeam);

  const commissionBaseMap = useAppSelector(selectCommissionBaseMap);

  const [activeSection, setActiveSection] = useState(SectionIds.CONDITIONS);
  const [showExample, setShowExample] = useState(false);
  const [createCommissionRule, createCommissionRuleState] = useCreateCommissionRuleMutation();
  const [updateCommissionRule, updateCommissionRuleState] = useUpdateCommissionRuleMutation();
  const [getCommissionRuleById, getCommissionRuleByIdState] = useLazyGetCommissionRuleByIdQuery();
  const [getCommissionBaseQuery] = useLazyGetCommissionBaseQuery();

  const [getAssignableTeamMemberQuery, { data: assignableTeamMembers, isSuccess: assignableTeamMembersSuccess }] =
    useLazyGetAssignableMembersQuery();
  const { data: externalCompaniesData } = useGetExternalCompaniesQuery(
    { teamId: currentTeam?.id?.toString() || '', status: CommissionStatus.ACTIVE },
    {
      skip: !currentTeam?.id
    }
  );
  const [getMerchantCustomizations, { data: merchantCustomizations }] = useLazyGetMerchantCustomizationsQuery();

  // State for form data
  const [conditions, setConditions] = useState<CommissionRuleCondition[]>([]);
  const [commissionRateData, setCommissionRateData] = useState({
    value: '',
    unit: CommissionType.ABSOLUTE,
    baseType: null
  });
  const [assignedMembers, setAssignedMembers] = useState<AssignableMember[]>([]);
  const [teamMembers, setTeamMembers] = useState<AssignableMember[]>([]);
  const [title, setTitle] = useState('');
  const [showAssignMembersModal, setShowAssignMembersModal] = useState(false);
  const [lockedMemberId, setLockedMemberId] = useState<string | null>(null);

  // Validation error states
  const [errors, setErrors] = useState({
    title: false,
    commissionRate: false,
    commissionBase: false
  });

  // Refs for scrolling to error sections
  const titleRef = useRef<HTMLDivElement>(null);
  const commissionRateRef = useRef<HTMLDivElement>(null);
  const conditionsRef = useRef<HTMLDivElement>(null);
  const membersRef = useRef<HTMLDivElement>(null);

  // Fetch assignable team members

  // Fetch team members
  useEffect(() => {
    if (currentTeam?.id) {
      getAssignableTeamMemberQuery(currentTeam.id.toString());
      getMerchantCustomizations({ teamId: currentTeam.id.toString() });
    }
    getCommissionBaseQuery();
  }, [currentTeam?.id]);

  useEffect(() => {
    if (assignableTeamMembersSuccess && assignableTeamMembers?.data?.teamMemberList) {
      setTeamMembers(assignableTeamMembers.data.teamMemberList);
      if (location.state?.memberId) {
        const memberToLock = assignableTeamMembers.data.teamMemberList.find(
          member => member.merchantId === location.state.memberId
        );
        if (memberToLock) {
          setLockedMemberId(memberToLock.merchantId);
          setAssignedMembers([memberToLock]);
        }
      }
    }
  }, [assignableTeamMembersSuccess, assignableTeamMembers]);

  // Fetch rule data if in edit mode
  useEffect(() => {
    if (isEditMode && id && currentTeam?.id) {
      getCommissionRuleById({ teamId: currentTeam.id.toString(), commissionRuleId: id });
    }
  }, [isEditMode, id, currentTeam?.id]);

  // rule edit mode
  useEffect(() => {
    if (getCommissionRuleByIdState.data) {
      const rule = getCommissionRuleByIdState.data;
      handleTitleChange(rule.title);

      if (rule.conditions && rule.conditions.length > 0) {
        setConditions(
          rule.conditions.map(condition => ({
            conditionIf: condition.conditionIf,
            conditionOp: condition.conditionOp as CommissionRuleCondition['conditionOp'],
            conditionValue: condition.conditionValue as CommissionRuleCondition['conditionValue']
          }))
        );
      }

      setCommissionRateData({
        value: rule.commissionValue.toString(),
        unit: rule.commissionType.toUpperCase() as CommissionType,
        baseType: rule?.commissionApplyOn ? commissionBaseMap[rule.commissionApplyOn] : null
      });

      // Fixed assigned members mapping
      if (rule.memberIds && rule.memberIds.length > 0 && teamMembers.length > 0) {
        const members = rule.memberIds.map(memberId => {
          const member = teamMembers.find(m => m.merchantId === memberId.id);
          if (member) {
            return member;
          }
          return {
            merchantId: memberId.id,
            name: memberId.name,
            profilePictureUrl: memberId.profilePictureURL,
            email: '',
            phone: ''
          } as AssignableMember;
        });
        handleMembersChange(members);
      }
    }
  }, [getCommissionRuleByIdState.data, teamMembers]);

  useEffect(() => {
    // Handle initial condition if provided
    if (location.state?.initialCondition && !isEditMode) {
      setConditions([location.state.initialCondition]);
    }
  }, [location.state, isEditMode]);

  const scrollToSection = (sectionId: SectionIds) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  // Form field handlers
  const handleConditionsChange = (newConditions: CommissionRuleCondition[]) => {
    setConditions(newConditions);
  };

  const handleCommissionRateChange = data => {
    setCommissionRateData(data);
    // Clear error when user updates the value
    if (data.value) {
      setErrors(prev => ({ ...prev, commissionRate: false }));
    }
    if (data.baseType?.key) {
      setErrors(prev => ({ ...prev, commissionBase: false }));
    }
  };

  const handleMembersChange = (members: AssignableMember[]) => {
    // Ensure locked member is always included
    if (lockedMemberId) {
      const lockedMember = teamMembers.find(member => member.merchantId === lockedMemberId);
      if (lockedMember && !members.some(m => m.merchantId === lockedMemberId)) {
        setAssignedMembers([...members, lockedMember]);
        return;
      }
    }
    setAssignedMembers(members);
  };

  const handleTitleChange = value => {
    setTitle(value);
    // Clear error when user updates the title
    if (value.trim()) {
      setErrors(prev => ({ ...prev, title: false }));
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {
      title: !title.trim(),
      commissionRate: !commissionRateData.value,
      commissionBase: commissionRateData.unit === CommissionType.PERCENTAGE && !commissionRateData.baseType?.key
    };

    setErrors(newErrors);

    // Return true if no errors
    return !Object.values(newErrors).some(error => error);
  };

  const scrollToError = () => {
    if (errors.title && titleRef.current) {
      titleRef.current.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(SectionIds.TITLE);
      return;
    }

    if ((errors.commissionRate || errors.commissionBase) && commissionRateRef.current) {
      commissionRateRef.current.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(SectionIds.COMMISSION_RATE);
    }
  };

  // Form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      scrollToError();
      return;
    }

    try {
      // Format conditions for API
      const formattedConditions = conditions
        .filter(condition => condition.conditionIf && condition.conditionValue.length > 0)
        .map(condition => condition);

      const commonPayload = {
        title: title.trim(),
        conditions: formattedConditions,
        commissionValue: parseFloat(commissionRateData.value),
        commissionType: commissionRateData.unit.toUpperCase() as CommissionType,
        commissionApplyOn: commissionRateData.baseType?.key,
        memberIDs: assignedMembers.map(member => member.merchantId)
      };

      const handleNavigation = () => {
        if (location.state?.returnTo) {
          navigate(location.state.returnTo, {
            state: location.state.returnToState,
            replace: true
          });
        } else {
          navigate(routesPath.COMMISSION_SETTINGS, {
            state: { showSuccessModal: true, isEdit: isEditMode },
            replace: true
          });
        }
      };

      if (isEditMode && id) {
        // Update existing rule
        const updatePayload: UpdateCommissionRulePayload = {
          ...commonPayload,
          teamId: currentTeam?.id?.toString() || '',
          commissionRuleId: id,
          status: getCommissionRuleByIdState.data.status
        };
        updateCommissionRule(updatePayload)
          .unwrap()
          .then(() => {
            logAnalyticEvent(SettingsCleverTapEvents.webSettingsCommissionRuleEdited);
            handleNavigation();
          });
      } else {
        // Create new rule
        const createPayload: CreateCommissionRulePayload = {
          ...commonPayload,
          teamId: currentTeam?.id?.toString() || '',
          status: CommissionStatus.ACTIVE
        };
        createCommissionRule(createPayload)
          .unwrap()
          .then(() => {
            handleNavigation();
          });
      }
    } catch (error) {
      // error
    }
  };

  const handleOpenAssignMembersModal = () => {
    setShowAssignMembersModal(true);
  };

  const handleCloseAssignMembersModal = () => {
    setShowAssignMembersModal(false);
  };

  const handleSaveMembers = (selectedMembers: AssignableMember[]) => {
    handleMembersChange(selectedMembers);
    handleCloseAssignMembersModal();
  };

  const handleCloseExample = () => {
    setShowExample(false);
  };

  const getBorderStyles = (sectionId: SectionIds) => {
    if (sectionId === SectionIds.COMMISSION_RATE) {
      if (errors.commissionRate || errors.commissionBase) {
        return 'border-error';
      }
    }
    if (sectionId === SectionIds.TITLE) {
      if (errors.title) {
        return 'border-error';
      }
    }
    return activeSection === sectionId ? 'border-secondary' : 'border-borderGray';
  };

  const isLoading = createCommissionRuleState.isLoading || updateCommissionRuleState.isLoading;

  if (isEditMode && getCommissionRuleByIdState.isLoading) {
    return <Loader />;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="sticky top-0 z-10 flex w-full border-b border-borderGray bg-white">
        <div className="flex h-[72px] w-[262px] items-center border-borderGray px-6">
          <BackIcon className="mr-5 cursor-pointer" onClick={() => navigate(-1)} />
          <span className="text-xl font-semibold text-primaryText">{isEditMode ? 'Edit rule' : 'Create rule'}</span>
        </div>
        <div className="flex h-[72px] flex-1 items-center justify-end px-[30px]">
          <div className="flex items-center gap-4">
            <CustomButton
              id="view-example"
              type={ButtonType.SECONDARY}
              childrenContainerStyles="text-[17px] font-semibold"
              className="h-10 border-none shadow-none hover:bg-transparent active:shadow-none"
              onClick={() => setShowExample(true)}>
              View example
            </CustomButton>
            <CustomButton
              id="save-button"
              type={ButtonType.PRIMARY}
              childrenContainerStyles="text-[17px] font-semibold"
              className="h-10"
              onClick={handleSubmit}
              isLoading={isLoading}>
              {isEditMode ? 'Save' : 'Create'}
            </CustomButton>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex h-[calc(100vh-72px)] overflow-hidden">
        <div className="sticky top-[72px] hidden h-full w-[262px] bg-white lg:flex">
          <div className="flex h-full flex-col gap-4 overflow-y-auto p-4 px-12">
            {sections.map(section => (
              <div
                key={section.id}
                className={`flex h-[49px] cursor-pointer items-center rounded-md px-4 text-17px font-semibold ${
                  activeSection === section.id ? 'border border-secondary text-secondary' : 'text-tertiaryText'
                }`}
                onClick={() => scrollToSection(section.id)}>
                {section.label}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="h-full w-full flex-1">
          <div className="customNormalScroll h-full w-full overflow-y-auto 2xl:ml-[120px]">
            <div className="w-full space-y-12 p-6 pb-24 text-headingGray xl:w-[85%] xl:px-8 2xl:max-w-[961px]">
              <section id="conditions" className="scroll-mt-[72px]" ref={conditionsRef}>
                <h2 className="mb-4 text-xl font-semibold">Conditions (optional)</h2>
                <div
                  className={`rounded-md border p-5 ${getBorderStyles(SectionIds.CONDITIONS)}`}
                  onFocus={() => setActiveSection(SectionIds.CONDITIONS)}>
                  <CommissionConditions
                    conditions={conditions}
                    onConditionsChange={handleConditionsChange}
                    externalCompanies={externalCompaniesData?.data || []}
                    handleOnFocus={() => setActiveSection(SectionIds.CONDITIONS)}
                    paymentMethods={merchantCustomizations?.paymentModes || []}
                  />
                </div>
              </section>

              <section
                id="commission-rate"
                className="scroll-mt-[72px]"
                ref={commissionRateRef}
                onFocus={() => setActiveSection(SectionIds.COMMISSION_RATE)}>
                <h2 className="mb-4 text-xl font-semibold">
                  Commission rate
                  <span className="text-red-500">*</span>
                </h2>
                <div className={`rounded-md border p-5 ${getBorderStyles(SectionIds.COMMISSION_RATE)}`}>
                  <CommissionRate onChange={handleCommissionRateChange} initialData={commissionRateData} />
                </div>
              </section>

              <section
                id="members"
                className="relative scroll-mt-[72px]"
                ref={membersRef}
                onFocus={() => setActiveSection(SectionIds.MEMBERS)}>
                <h2 className="mb-4 text-xl font-semibold">Assign team members</h2>
                <div className={`rounded-md border p-5 ${getBorderStyles(SectionIds.MEMBERS)}`}>
                  <AssignTeamMembers
                    onMembersChange={handleMembersChange}
                    membersList={teamMembers}
                    initialSelectedMembers={assignedMembers}
                    onOpenModal={handleOpenAssignMembersModal}
                    lockedMemberId={lockedMemberId}
                  />
                </div>
              </section>

              <section
                id="title"
                className="scroll-mt-[72px] pb-20"
                ref={titleRef}
                onFocus={() => setActiveSection(SectionIds.TITLE)}>
                <h2 className="mb-4 text-xl font-semibold">
                  Title
                  <span className="text-red-500">*</span>
                </h2>
                <div className={`rounded-md border p-5 ${getBorderStyles(SectionIds.TITLE)}`}>
                  <div className="flex flex-col">
                    <CustomInput
                      id="rule-title"
                      name="rule-title"
                      placeholder="Enter rule title"
                      value={title}
                      onChange={handleTitleChange}
                      className={`w-full rounded-md p-3 focus:outline-none ${errors.title ? 'border-red-500' : ''}`}
                    />
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
      {showAssignMembersModal && (
        <AssignableMembersModal
          list={teamMembers}
          selectedMembers={assignedMembers}
          onClose={handleCloseAssignMembersModal}
          onSave={handleSaveMembers}
          lockedMemberId={lockedMemberId}
        />
      )}

      {showExample && (
        <CommissionCalculationExample
          isOpen={showExample}
          onClose={handleCloseExample}
          commissionValue={commissionRateData.value}
          baseType={commissionRateData.baseType}
          commissionType={commissionRateData.unit}
        />
      )}
    </div>
  );
};

export default CreateCommissionRule;
